import React, { useState, useEffect, useRef, useCallback } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { CircularProgress } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { ExportHubAPI, ExportScreeningRun } from 'api/ExportHubAPI';

interface Props {
  databaseId: string;
  groupId: string | undefined;
  // Called when a run finishes so the parent can refresh product rows/statuses.
  onComplete: () => void;
}

const POLL_MS = 2500;

// Triggers a deterministic classification run over the whole database (screening
// plan Step 4) and shows live progress by polling the run header. On completion
// it refreshes the products so updated compliance statuses appear.
const ClassificationRunControl: React.FC<Props> = ({
  databaseId,
  groupId,
  onComplete,
}) => {
  const [run, setRun] = useState<ExportScreeningRun | null>(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback((): void => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const isRunning = run?.status === 'processing';

  useEffect(() => {
    if (!isRunning || !run || !groupId) return undefined;
    timer.current = setInterval(async () => {
      try {
        const latest = await ExportHubAPI.getScreeningRun(run.id, groupId);
        if (!latest) return;
        setRun(latest);
        if (latest.status !== 'processing') {
          stopPolling();
          const verb =
            latest.status === 'failed' ? 'failed' : 'finished';
          enqueueSnackbar(
            `Classification ${verb}: ${latest.products_processed} processed, ${latest.products_flagged} flagged.`,
            {
              variant: latest.status === 'failed' ? 'error' : 'success',
            },
          );
          onComplete();
        }
      } catch {
        // transient poll error — keep polling
      }
    }, POLL_MS);
    return stopPolling;
  }, [isRunning, run, groupId, stopPolling, onComplete]);

  const handleStart = async (): Promise<void> => {
    if (!groupId) return;
    setStarting(true);
    try {
      const started = await ExportHubAPI.startClassification(databaseId, groupId);
      setRun(started);
      if (started.products_total === 0) {
        enqueueSnackbar(
          'No products with an ECCN to classify — add ECCNs first.',
          { variant: 'info' },
        );
      } else {
        enqueueSnackbar(
          `Classifying ${started.products_total} product${
            started.products_total === 1 ? '' : 's'
          }…`,
          { variant: 'info' },
        );
      }
    } catch (e: any) {
      enqueueSnackbar(
        e?.response?.data?.message ||
          (e instanceof Error ? e.message : 'Could not start classification.'),
        { variant: 'error' },
      );
    } finally {
      setStarting(false);
    }
  };

  if (isRunning && run) {
    const pct = run.products_total
      ? Math.round((run.products_processed / run.products_total) * 100)
      : 0;
    return (
      <span className='eh-classify-progress' title='Classification in progress'>
        <CircularProgress size={14} />
        Classifying… {run.products_processed}/{run.products_total} ({pct}%)
      </span>
    );
  }

  return (
    <button
      className='btn-outline'
      type='button'
      onClick={handleStart}
      disabled={starting || !groupId}
      style={{ opacity: starting || !groupId ? 0.6 : 1 }}
    >
      <AutoAwesomeIcon style={{ fontSize: 18 }} />{' '}
      {starting ? 'Starting…' : 'Run Classification'}
    </button>
  );
};

export default ClassificationRunControl;
